/** @format */

import { makeStyles } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import GeneralDialog from '../../../components/Dialog/GeneralDialog';
import InputFormRest from '../../../components/Form/InputFormRest';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { configDefault } from '../../../core/config';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { NotificationHelper } from '../../../helpers/NotificationHelper';
import { PricelistSchemaHelper } from '../../../helpers/PricelistSchemaHelper';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import PricelistSchemaElementInterface from '../../../interfaces/billing/PricelistSchemaElementInterface';
import PricelistSchemaInterface from '../../../interfaces/billing/PricelistSchemaInterface';
import { pricelistSchemaConstants, pricelistSchemaElementConstants } from '../../../modules/pricelists/reducers';

interface PricelistCloneDialogProps {
    clonePricelistHandler: (param: { id: number; type: string } | null) => void;
    openCloneModal: boolean;
    cloneParams: { id: number; type: string } | null;
    loadPricelistSchemaList: (uri?: string) => void;
    loadPricelistSchemaElementList: (uri?: string) => void;
}

export const PricelistCloneDialog = (props: PricelistCloneDialogProps): React.ReactElement => {
    const dispatch = useDispatch();
    const useStyle = makeStyles(adescomStyle);
    const classes = useStyle();

    const PRICELIST_SCHEMA_URI = '/pricelist_schemas';
    const PRICELIST_SCHEMA_ELEMENT_URI = '/pricelist_schema_elements';

    const [entity, setEntity] = useState<PricelistSchemaInterface | PricelistSchemaElementInterface | null>(null);
    const [oldEntity, setOldEntity] = useState<PricelistSchemaInterface | PricelistSchemaElementInterface | null>(null);

    const saveNewPricelist = (): void => {
        let uri = null;
        let callback = (): void => {};
        if (props.cloneParams) {
            switch (props.cloneParams.type) {
                case PricelistSchemaHelper.PRICELIST_CLONE_TYPE.PRICELIST_SCHEMA:
                    uri = PRICELIST_SCHEMA_URI;
                    callback = props.loadPricelistSchemaList;
                    break;
                case PricelistSchemaHelper.PRICELIST_CLONE_TYPE.PRICELIST_SCHEMA_ELEMENT:
                    uri = PRICELIST_SCHEMA_ELEMENT_URI;
                    callback = props.loadPricelistSchemaElementList;
                    break;
                default:
                    break;
            }

            if (uri) {
                if (entity) {
                    const oldEntityId = entity.id;
                    entity.id = null;
                    let params: any = {};
                    params[PricelistSchemaHelper.PRICELIST_SCHEMA_ELEMENT_CONSTANTS.SCHEMA_CLONE_ID] = oldEntityId;
                    entity.params = JSON.stringify(params);
                    const itemLoaders = new ItemLoaders(dispatch);
                    itemLoaders.postWithCallback(
                        FormGeneralHelper.getByUri(FormGeneralHelper.getPath(uri, configDefault.PLATFORM_API_PREFIX)),
                        pricelistSchemaConstants.ADD,
                        entity,
                        (data: PricelistSchemaInterface) => {
                            callback();
                        },
                        {
                            propagateDataToCallback: true,
                        }
                    );
                }
            }
        }
    };

    const closeCloneDialog = (): void => {
        props.clonePricelistHandler(null);
    };

    const changeInput = (e: any, stateName: string, type: string, require: boolean, maxValue: number, minValue: number): void => {
        // TODO: Add validation (use entityStatus)
        if (entity) {
            setEntity({ ...entity, [stateName]: e.target.value });
        }
    };

    const loadPricelistSchema = useCallback(
        (id: number): void => {
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`${PRICELIST_SCHEMA_URI}/${id}`, configDefault.PLATFORM_API_PREFIX)),
                pricelistSchemaConstants.ITEM,
                (data: PricelistSchemaInterface) => {
                    setEntity(data);
                    setOldEntity(data);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch]
    );
    const loadPricelistSchemaElement = useCallback(
        (id: number): void => {
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`${PRICELIST_SCHEMA_ELEMENT_URI}/${id}`, configDefault.PLATFORM_API_PREFIX)),
                pricelistSchemaElementConstants.ITEM,
                (data: PricelistSchemaElementInterface) => {
                    setEntity(data);
                    setOldEntity(data);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch]
    );
    useEffect(() => {
        if (props.cloneParams) {
            switch (props.cloneParams.type) {
                case PricelistSchemaHelper.PRICELIST_CLONE_TYPE.PRICELIST_SCHEMA:
                    loadPricelistSchema(props.cloneParams.id);
                    break;
                case PricelistSchemaHelper.PRICELIST_CLONE_TYPE.PRICELIST_SCHEMA_ELEMENT:
                    loadPricelistSchemaElement(props.cloneParams.id);
                    break;
                default:
                    break;
            }
        }
    }, [props.cloneParams, loadPricelistSchemaElement, loadPricelistSchema]);

    return (
        <GeneralDialog
            maxWidth={'md'}
            open={props.openCloneModal}
            dialogType={'action'}
            fullWidth
            title={'pricelists:t.add_pricelist_element_form'}
            disagreeCallback={closeCloneDialog}
            disagreeCallbackTextLabel={'general:common.cancel'}
            agreeCallback={UserRoleHelper.checkRole(roles.ROLE_PLATFORM_PRICELIST_SCHEMA_ELEMENT_PUT) ? saveNewPricelist : (): void => {}}
            agreeCallbackTextLabel={'general:common.save'}
            agreeButtonDisabled={entity?.mainname === oldEntity?.mainname}
        >
            <GridContainer style={{ height: 200 }}>
                <GridItem xs={12} md={12} lg={12}>
                    <InputFormRest
                        classes={classes}
                        callback={changeInput}
                        stateName="mainname"
                        typeCallback={FormGeneralHelper.TYPE_TEXT}
                        typeField={FormGeneralHelper.TYPE_TEXT}
                        displayFieldName={
                            props.cloneParams?.type === PricelistSchemaHelper.PRICELIST_CLONE_TYPE.PRICELIST_SCHEMA
                                ? 'pricelists:h.pricelist_schema_name'
                                : 'pricelists:h.pricelist_schema_element_name'
                        }
                        required={false}
                        vMax={255}
                        vMin={0}
                        value={entity ? entity.mainname : ''}
                    />
                </GridItem>
            </GridContainer>
        </GeneralDialog>
    );
};
